/**
 * Recipe book side panels for the container screens.
 *
 *  - attachRecipeBook(): survival inventory (2x2) and crafting table (3x3). Search box,
 *    category tabs, "craftable only" toggle, paged result grid, ghost recipe in the crafting
 *    grid, click-again / Fill / shift-click auto-fill. Every recipe is shown (no unlock gating).
 *  - attachRecipeList(): read-only recipe lists (furnace, brewing stand, smithing table).
 *
 * Panels hook into a screen instance (pointer, wheel, keys, tooltip, layout, update) instead of
 * editing ContainerScreen, so the shared click engine stays untouched. Hit testing is done in
 * GUI px against the regions each panel builds while rendering, so mouse and touch (the touch
 * layer translates taps to mouse events on the stage) share one code path. On coarse pointers
 * every target is at least 40 CSS px.
 */

import { BRICKS_ID, CRAFTING_TABLE_ID, LOG_ID } from '../../blocks';
import {
  CATEGORY_LABEL, allRecipes, countStacks, fitsGrid, ingredientIds, ingredientLines,
  planFill, planMultiplier, type Category, type ListRow, type RecipeEntry,
} from '../../game/recipeBook';
import { I } from '../../game/itemIds';
import { stackName } from '../../items';
import { isPotionItem, potionTooltipLines } from '../../game/brewing/potions';
import { clearStack, isEmpty, setStack, stackOf, type Stack } from '../../game/types';
import { ItemIcons } from '../itemIcons';
import { Label, sprite, textWidth, type GuiStage } from '../kit';
import type { ScreenDeps } from './screenHost';

// ------------------------------------------------------------------ plumbing ---

/** The parts of a ContainerScreen the panels reach into (all exist on every screen). */
interface Host {
  root: HTMLDivElement;
  deps: ScreenDeps;
  stage: GuiStage;
  w: number; h: number;
  left: number; top: number;
  mx: number; my: number;
  layout(stageW: number, stageH: number): void;
  pointerMove(cx: number, cy: number): void;
  pointerDown(button: number, shift: boolean): void;
  keyDown(e: KeyboardEvent): boolean;
  update(dt: number): void;
  refresh(force: boolean): void;
  tipLines(): string[] | null;
  wheel?: (deltaY: number) => void;
}

interface Region {
  x: number; y: number; w: number; h: number;
  down?: (button: number, shift: boolean) => void;
  tip?: () => string[] | null;
}

const coarsePointer = (): boolean => {
  try { return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches; } catch { return false; }
};

/** Sizes in GUI px: vanilla's, grown on touch devices so targets reach 40 CSS px. */
function geometry(S: number): { coarse: boolean; cell: number; bar: number; btn: number; tab: [number, number]; rowH: number } {
  const coarse = coarsePointer();
  const t = Math.ceil(40 / S);
  return {
    coarse,
    cell: coarse ? Math.max(25, t) : 25,
    bar: coarse ? Math.max(14, t) : 14,
    btn: coarse ? Math.max(20, t) : 20,
    tab: coarse ? [Math.max(35, t), Math.max(27, t)] : [35, 27],
    rowH: coarse ? Math.max(22, t) : 22,
  };
}

/** A 16x16 item icon, scaled about its top-left. */
function iconEl(stack: Stack, x: number, y: number, scale = 1, opacity = 1): HTMLDivElement {
  const d = document.createElement('div');
  d.style.cssText =
    `position:absolute;left:${x}px;top:${y}px;width:16px;height:16px;transform-origin:0 0;transform:scale(${scale});` +
    `pointer-events:none;${opacity < 1 ? `opacity:${opacity};` : ''}`;
  ItemIcons.decorate(d, stack);
  return d;
}

function panelBg(x: number, y: number, w: number, h: number): HTMLDivElement {
  const d = document.createElement('div');
  d.style.cssText =
    `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:${h}px;box-sizing:border-box;background:#c6c6c6;` +
    'border:1px solid #000;box-shadow:inset 2px 2px 0 #fff,inset -2px -2px 0 #555;pointer-events:none;';
  return d;
}

function box(x: number, y: number, w: number, h: number, css: string): HTMLDivElement {
  const d = document.createElement('div');
  d.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:${h}px;box-sizing:border-box;pointer-events:none;${css}`;
  return d;
}

function textAt(text: string, x: number, y: number, color = '#404040', align: 'left' | 'center' | 'right' = 'left', shadow = false): HTMLCanvasElement {
  return new Label(text, x, y, { color, shadow, align }).el;
}

const tipOfStack = (s: Stack): string[] => {
  const extra = isPotionItem(s.id) ? potionTooltipLines(s.id, s.dmg) : [];
  return [stackName(s), ...extra];
};

/** Common behaviour of a left-hand panel that shifts the screen right while open. */
abstract class SidePanel {
  open = false;
  protected readonly el: HTMLDivElement;
  protected regions: Region[] = [];
  protected hover: Region | null = null;
  /** Panel width / height in GUI px and its top-left in screen-root coordinates. */
  protected pw = 147; protected ph = 166; protected px = 0; protected py = 0;
  protected get g(): ReturnType<typeof geometry> { return geometry(this.sc.stage.scale); }
  private dirty = true;

  constructor(protected readonly sc: Host, private readonly btnPos: { x: number; y: number }) {
    this.el = document.createElement('div');
    this.el.style.cssText = 'position:absolute;left:0;top:0;width:0;height:0;z-index:4;pointer-events:none;';
    sc.root.appendChild(this.el);
  }

  protected abstract size(): { w: number; h: number };
  protected abstract build(): void;
  abstract hint(): string;

  protected invalidate(): void { this.dirty = true; }

  /** Toggle button rect (screen-root coordinates). */
  protected buttonRect(): { x: number; y: number; w: number; h: number } {
    const b = this.g.btn;
    if (this.g.coarse) return { x: this.sc.w + 4, y: this.btnPos.y < 30 ? 6 : 6 + b + 6, w: b, h: b };
    return { x: this.btnPos.x, y: this.btnPos.y, w: b, h: Math.round(b * 0.9) };
  }

  setOpen(v: boolean): void {
    this.open = v;
    this.relayout();
    this.invalidate();
  }

  protected relayout(): void {
    this.sc.layout(this.sc.stage.width, this.sc.stage.height);
    this.sc.refresh(true);
  }

  /** Called after the screen computed its centred position. */
  afterLayout(): void {
    if (!this.open) return;
    const { w } = this.size();
    const shift = Math.ceil((w + 1) / 2);
    const left = Math.max(this.sc.left + shift, w + 31);
    this.sc.left = left;
    this.sc.root.style.left = left + 'px';
  }

  render(): void {
    this.dirty = false;
    this.el.replaceChildren();
    this.regions = [];
    // toggle button (always present)
    const r = this.buttonRect();
    const hl = this.hoverBtn;
    this.el.appendChild(sprite(`gui/sprites/recipe_book/button${hl ? '_highlighted' : ''}.png`, r.x, r.y, r.w, r.h));
    const bs = Math.max(0.6, r.w / 20) * 0.7;
    this.el.appendChild(iconEl(stackOf(I.BOOK), r.x + (r.w - 16 * bs) / 2, r.y + (r.h - 16 * bs) / 2, bs));
    this.regions.push({ ...r, down: () => this.setOpen(!this.open), tip: () => [this.open ? 'Hide recipes' : this.hint()] });
    if (this.open) {
      const sz = this.size();
      this.pw = sz.w; this.ph = sz.h;
      this.px = -(this.pw + 1);
      this.py = Math.min(0, Math.floor((166 - this.ph) / 2));
      this.build();
    }
    this.hover = this.regionAt(this.lastX, this.lastY);
  }
  private lastX = -999; private lastY = -999;
  private hoverBtn = false;

  // --- input (coordinates are screen-root GUI px) ---
  private regionAt(lx: number, ly: number): Region | null {
    for (let i = this.regions.length - 1; i >= 0; i--) {
      const r = this.regions[i];
      if (lx >= r.x && lx < r.x + r.w && ly >= r.y && ly < r.y + r.h) return r;
    }
    return null;
  }
  /** True when the point is on this panel (so the screen must not treat it as "outside"). */
  covers(lx: number, ly: number): boolean {
    if (this.regionAt(lx, ly)) return true;
    if (!this.open) return false;
    const tx = this.px - this.g.tab[0] + 5;
    return lx >= tx && lx < this.px + this.pw && ly >= this.py && ly < this.py + this.ph;
  }
  move(lx: number, ly: number): void {
    this.lastX = lx; this.lastY = ly;
    this.hover = this.regionAt(lx, ly);
    const btn = this.buttonRect();
    const onBtn = lx >= btn.x && lx < btn.x + btn.w && ly >= btn.y && ly < btn.y + btn.h;
    if (onBtn !== this.hoverBtn) { this.hoverBtn = onBtn; this.invalidate(); }
  }
  down(lx: number, ly: number, button: number, shift: boolean): boolean {
    const r = this.regionAt(lx, ly);
    if (r?.down) { r.down(button, shift); return true; }
    return this.covers(lx, ly);
  }
  tip(): string[] | null { return this.hover?.tip?.() ?? null; }
  wheel(_dy: number): boolean { return false; }
  key(_e: KeyboardEvent): boolean { return false; }
  update(_dt: number): void { if (this.dirty || this.needsRender()) this.render(); }
  protected needsRender(): boolean { return false; }
}

function hook(host: Host, panel: SidePanel): void {
  const origLayout = host.layout.bind(host);
  host.layout = (sw, sh) => { origLayout(sw, sh); panel.afterLayout(); };
  const origMove = host.pointerMove.bind(host);
  host.pointerMove = (cx, cy) => {
    const S = host.stage.scale;
    panel.move(cx / S - host.left, cy / S - host.top);
    origMove(cx, cy);
  };
  const origDown = host.pointerDown.bind(host);
  host.pointerDown = (button, shift) => {
    if (button === 0 || button === 1) {
      const lx = host.mx - host.left, ly = host.my - host.top;
      if (panel.down(lx, ly, button, shift)) { host.refresh(true); return; }
    }
    origDown(button, shift);
  };
  const origKey = host.keyDown.bind(host);
  host.keyDown = (e) => panel.key(e) || origKey(e);
  const origUpdate = host.update.bind(host);
  host.update = (dt) => { panel.update(dt); origUpdate(dt); };
  const origTip = host.tipLines.bind(host);
  host.tipLines = () => panel.tip() ?? origTip();
  const origWheel = host.wheel?.bind(host);
  host.wheel = (dy) => { if (!panel.wheel(dy)) origWheel?.(dy); };
  panel.render();
}

// ----------------------------------------------------------------- recipe book ---

export interface RecipeBookOpts {
  gw: number; gh: number;
  /** Crafting grid cell origins (row-major) and the result slot, screen-root GUI px. */
  cells: Array<{ x: number; y: number }>;
  result: { x: number; y: number };
  grid: Stack[];
  /** Recompute the result slot after the grid changed. */
  onGrid: () => void;
  /** Vanilla toggle-button position. */
  button: { x: number; y: number };
}

type CatTab = Category | 'all';

class RecipeBookPanel extends SidePanel {
  private tab: CatTab = 'all';
  private query = '';
  private focused = false;
  private craftableOnly = false;
  private page = 0;
  private selected: RecipeEntry | null = null;
  private blink = 0;
  private sig = '';
  private ghostKey = '';
  private readonly ghostEls: HTMLDivElement[] = [];
  private readonly ghostLayer: HTMLDivElement;
  private cache: { key: string; list: RecipeEntry[] } | null = null;
  private avail = new Map<number, number>();

  constructor(sc: Host, private readonly o: RecipeBookOpts) {
    super(sc, o.button);
    this.ghostLayer = box(0, 0, 0, 0, 'z-index:6;');
    sc.root.appendChild(this.ghostLayer);
  }

  hint(): string { return 'Recipe Book'; }

  protected size(): { w: number; h: number } {
    const c = this.g.cell, bar = this.g.bar;
    const w = 22 + 5 * c;
    const h = 13 + bar + 4 + 4 * c + 6 + this.g.btn - 3 + 8;
    return { w, h: Math.max(166, h) };
  }

  private availability(): Map<number, number> {
    const m = countStacks(this.sc.deps.inventory.slots);
    return countStacks(this.o.grid, m);
  }

  private visible(): RecipeEntry[] {
    const q = this.query.trim().toLowerCase();
    const key = `${this.tab}|${q}|${this.craftableOnly}|${this.o.gw}|${this.sig}`;
    if (this.cache?.key === key) return this.cache.list;
    const { gw, gh } = this.o;
    const list: RecipeEntry[] = [];
    const late: RecipeEntry[] = [];
    for (const e of allRecipes()) {
      if (this.tab !== 'all' && e.category !== this.tab) continue;
      if (q && !e.search.includes(q)) continue;
      const fits = fitsGrid(e.recipe, gw, gh);
      if (this.craftableOnly && !(fits && planFill(e.recipe, gw, gh, this.avail))) continue;
      (fits ? list : late).push(e); // 3x3-only recipes sit after the ones that fit the 2x2 grid
    }
    list.push(...late);
    this.cache = { key, list };
    return list;
  }

  protected build(): void {
    const { px, py, pw, ph, g } = this;
    const c = g.cell, bar = g.bar;
    this.avail = this.availability();
    const list = this.visible();
    const cols = 5, rows = 4, per = cols * rows;
    const pages = Math.max(1, Math.ceil(list.length / per));
    this.page = Math.min(this.page, pages - 1);

    this.el.appendChild(panelBg(px, py, pw, ph));

    // category tabs (left edge, overlapping the panel by 5px)
    const [tw, th] = g.tab;
    const tabs: Array<{ id: CatTab; icon: Stack; name: string }> = [
      { id: 'all', icon: stackOf(I.BOOK), name: 'All' },
      { id: 'construction', icon: stackOf(BRICKS_ID), name: CATEGORY_LABEL.construction },
      { id: 'equipment', icon: stackOf(I.IRON_PICKAXE), name: CATEGORY_LABEL.equipment },
      { id: 'items', icon: stackOf(I.REDSTONE), name: CATEGORY_LABEL.items },
      { id: 'nature', icon: stackOf(LOG_ID), name: CATEGORY_LABEL.nature },
    ];
    tabs.forEach((t, i) => {
      const x = px - tw + 5, y = py + 3 + i * (th + 1);
      const on = this.tab === t.id;
      this.el.appendChild(sprite(`gui/sprites/recipe_book/tab${on ? '_selected' : ''}.png`, x - (on ? 2 : 0), y, tw + (on ? 2 : 0), th));
      const s = Math.min(1.6, th / 27 * 1.1);
      this.el.appendChild(iconEl(t.icon, x + (tw - 16 * s) / 2 - 1, y + (th - 16 * s) / 2, s));
      this.regions.push({ x, y, w: tw, h: th, down: () => { this.tab = t.id; this.page = 0; this.invalidate(); }, tip: () => [t.name] });
    });

    // search field
    const sx = px + 25, sy = py + 13, sw = pw - 66;
    this.el.appendChild(box(sx, sy, sw, bar, `background:#000;border:1px solid ${this.focused ? '#fff' : '#a0a0a0'};`));
    const shown = this.query || (this.focused ? '' : 'Search...');
    const txt = this.focused && Math.floor(this.blink * 2) % 2 === 0 ? shown + '_' : shown;
    let clip = txt;
    while (clip.length > 1 && textWidth(clip) > sw - 6) clip = clip.slice(1);
    this.el.appendChild(textAt(clip, sx + 3, sy + Math.floor((bar - 8) / 2) + 1, this.query ? '#ffffff' : '#808080', 'left', true));
    this.regions.push({
      x: sx, y: sy, w: sw, h: bar,
      down: () => {
        if (g.coarse && typeof prompt === 'function') {
          const v = prompt('Search recipes', this.query);
          if (v !== null) { this.query = v; this.page = 0; }
        } else this.focused = true;
        this.invalidate();
      },
    });

    // "craftable only" toggle
    const fw = Math.max(26, Math.round(bar * 1.6)), fh = bar + 2;
    const fx = px + pw - fw - 10, fy = py + 12;
    this.el.appendChild(sprite(`gui/sprites/recipe_book/filter_${this.craftableOnly ? 'enabled' : 'disabled'}.png`, fx, fy, fw, fh));
    this.regions.push({
      x: fx, y: fy, w: fw, h: fh,
      down: () => { this.craftableOnly = !this.craftableOnly; this.page = 0; this.invalidate(); },
      tip: () => [this.craftableOnly ? 'Showing craftable recipes' : 'Showing all recipes'],
    });

    // result grid
    const gx = px + 11, gy = py + 13 + bar + 4;
    const iconS = c / 25;
    for (let i = 0; i < per; i++) {
      const e = list[this.page * per + i];
      if (!e) break;
      const x = gx + (i % cols) * c, y = gy + Math.floor(i / cols) * c;
      const fits = fitsGrid(e.recipe, this.o.gw, this.o.gh);
      const can = fits && planFill(e.recipe, this.o.gw, this.o.gh, this.avail) !== null;
      this.el.appendChild(sprite(`gui/sprites/recipe_book/slot_${can ? 'craftable' : 'uncraftable'}.png`, x, y, c, c));
      const ic = iconEl(e.recipe.result, x + (c - 16 * iconS) / 2, y + (c - 16 * iconS) / 2, iconS, can ? 1 : 0.4);
      this.el.appendChild(ic);
      if (!fits) {
        this.el.appendChild(iconEl(stackOf(CRAFTING_TABLE_ID), x + c - 11 * iconS - 1, y + c - 11 * iconS - 1, 0.6 * iconS, 0.9)); // crafting table badge
      }
      if (this.selected === e) this.el.appendChild(box(x, y, c, c, 'outline:2px solid #ffff55;outline-offset:-2px;'));
      this.regions.push({
        x, y, w: c, h: c,
        down: (_b, shift) => this.click(e, can, shift),
        tip: () => this.recipeTip(e, fits, can),
      });
    }
    if (list.length === 0) {
      this.el.appendChild(textAt('No recipes found', px + pw / 2, gy + 20, '#404040', 'center'));
    }

    // pager + fill button
    const ay = gy + rows * c + 6, ah = g.btn - 3;
    const aw = Math.max(12, Math.round(ah * 12 / 17));
    const pgs = (name: string, x: number, delta: number, ok: boolean): void => {
      if (!ok) return;
      this.el.appendChild(sprite(`gui/sprites/recipe_book/${name}.png`, x, ay, aw, ah));
      this.regions.push({ x, y: ay, w: aw, h: ah, down: () => { this.page = (this.page + delta + pages) % pages; this.invalidate(); } });
    };
    pgs('page_backward', px + 8, -1, pages > 1);
    pgs('page_forward', px + pw - 8 - aw, 1, pages > 1);
    this.el.appendChild(textAt(`${this.page + 1}/${pages}`, px + 8 + aw + 4, ay + Math.floor((ah - 8) / 2), '#404040'));
    const bw = Math.max(40, Math.round(ah * 2.4)), bx = px + pw - 8 - aw - 4 - bw;
    const canFill = !!this.selected && fitsGrid(this.selected.recipe, this.o.gw, this.o.gh) &&
      planFill(this.selected.recipe, this.o.gw, this.o.gh, this.avail) !== null;
    this.el.appendChild(box(bx, ay, bw, ah, `background:${canFill ? '#6c6c6c' : '#8b8b8b'};border:1px solid #000;box-shadow:inset 1px 1px 0 ${canFill ? '#a8a8a8' : '#b4b4b4'},inset -1px -1px 0 #444;`));
    this.el.appendChild(textAt('Fill', bx + Math.floor(bw / 2), ay + Math.floor((ah - 8) / 2), canFill ? '#ffffff' : '#707070', 'center', canFill));
    this.regions.push({
      x: bx, y: ay, w: bw, h: ah,
      down: (_b, shift) => { if (this.selected) this.fill(this.selected, shift); },
      tip: () => (canFill ? ['Fill the crafting grid', '§7Shift: as many as possible'] : ['Select a craftable recipe']),
    });
  }

  private recipeTip(e: RecipeEntry, fits: boolean, can: boolean): string[] {
    const lines = [...tipOfStack(e.recipe.result)];
    if (e.recipe.result.count > 1) lines[0] += ` x${e.recipe.result.count}`;
    if (!fits) lines.push('§cRequires a Crafting Table');
    else if (!can) lines.push('§7Missing ingredients');
    lines.push(...ingredientLines(e.recipe).map((l) => '§7' + l));
    return lines;
  }

  private click(e: RecipeEntry, can: boolean, shift: boolean): void {
    const fits = fitsGrid(e.recipe, this.o.gw, this.o.gh);
    if (this.selected === e || (shift && can)) { if (fits) this.fill(e, shift); return; }
    this.selected = e;
    this.ghostKey = '';
    this.invalidate();
  }

  /** Clear the grid back into the inventory and place the recipe's ingredients (x1, or max on shift). */
  private fill(e: RecipeEntry, shift: boolean): void {
    const { gw, gh, grid } = this.o;
    const inv = this.sc.deps.inventory;
    const avail = this.availability();
    const plan = planFill(e.recipe, gw, gh, avail);
    if (!plan) return;
    const k = shift ? planMultiplier(plan, avail) : 1;
    for (const s of grid) {
      if (isEmpty(s)) continue;
      const left = inv.add(s.id, s.count, s.dmg, s.ench);
      if (left > 0) this.sc.deps.dropStack({ ...s, count: left });
      clearStack(s);
    }
    plan.cells.forEach((id, i) => {
      if (!id) return;
      inv.remove(id, k);
      setStack(grid[i], { id, count: k, dmg: 0 });
    });
    this.o.onGrid();
    this.sc.deps.onChange();
    this.ghostKey = '';
    this.invalidate();
  }

  override key(e: KeyboardEvent): boolean {
    if (!this.open || !this.focused) return false;
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    if (e.key === 'Enter') { this.focused = false; this.invalidate(); return true; }
    if (e.key === 'Backspace') this.query = this.query.slice(0, -1);
    else if (e.key.length === 1 && textWidth(this.query + e.key) < this.pw - 70) this.query += e.key;
    else return false;
    this.page = 0;
    this.invalidate();
    return true;
  }

  override wheel(dy: number): boolean {
    if (!this.open) return false;
    const pages = Math.max(1, Math.ceil(this.visible().length / 20));
    this.page = Math.min(pages - 1, Math.max(0, this.page + (dy > 0 ? 1 : -1)));
    this.invalidate();
    return true;
  }

  override down(lx: number, ly: number, button: number, shift: boolean): boolean {
    const hit = super.down(lx, ly, button, shift);
    // clicking anywhere but the search field drops its focus (so E / 1-9 work again)
    if (this.focused && !this.regionIsSearch(lx, ly)) { this.focused = false; this.invalidate(); }
    return hit;
  }
  private regionIsSearch(lx: number, ly: number): boolean {
    const sx = this.px + 25, sy = this.py + 13;
    return lx >= sx && lx < sx + this.pw - 66 && ly >= sy && ly < sy + this.g.bar;
  }

  protected override needsRender(): boolean {
    // availability changed (inventory / grid edits) or the search caret blinked
    const inv = this.sc.deps.inventory.slots;
    let h = 0;
    for (const s of inv) h = (h * 31 + s.id * 7 + s.count) | 0;
    for (const s of this.o.grid) h = (h * 31 + s.id * 7 + s.count) | 0;
    const sig = String(h);
    if (sig !== this.sig) { this.sig = sig; return this.open; }
    return false;
  }

  override update(dt: number): void {
    if (this.focused && this.open) {
      const prev = Math.floor(this.blink * 2);
      this.blink += dt;
      if (Math.floor(this.blink * 2) !== prev) this.invalidate();
    }
    super.update(dt);
    this.updateGhost();
  }

  /** Ghost ingredients in empty grid cells (alternatives cycle once a second). */
  private updateGhost(): void {
    const e = this.open ? this.selected : null;
    const phase = Math.floor(performance.now() / 1000);
    const { gw, gh, grid, cells } = this.o;
    let key = '';
    const ghosts: Array<{ stack: Stack; x: number; y: number }> = [];
    if (e && fitsGrid(e.recipe, gw, gh)) {
      const r = e.recipe;
      for (let i = 0; i < gw * gh; i++) {
        let ing: number | readonly number[] = 0;
        if (r.shaped) {
          const cx = i % gw, cy = Math.floor(i / gw);
          if (cx < r.w && cy < r.h) ing = r.ing[cy * r.w + cx];
        } else if (i < r.ing.length) ing = r.ing[i];
        const ids = ingredientIds(ing);
        if (ids.length === 0 || !isEmpty(grid[i])) continue;
        const id = ids[phase % ids.length];
        key += `${i}:${id};`;
        ghosts.push({ stack: stackOf(id), x: cells[i].x, y: cells[i].y });
      }
      if (grid.every((st) => isEmpty(st))) {
        ghosts.push({ stack: { ...r.result }, x: this.o.result.x, y: this.o.result.y });
        key += `r${r.result.id}`;
      }
    }
    if (key === this.ghostKey) return;
    this.ghostKey = key;
    this.ghostLayer.replaceChildren();
    this.ghostEls.length = 0;
    for (const gh2 of ghosts) {
      const slotBg = box(gh2.x - 1, gh2.y - 1, 18, 18, 'background:rgba(120,200,120,0.35);');
      this.ghostLayer.appendChild(slotBg);
      this.ghostLayer.appendChild(iconEl(gh2.stack, gh2.x, gh2.y, 1, 0.55));
    }
  }
}

/** Hook a recipe book into a 2x2 / 3x3 crafting screen (call after its grid exists). */
export function attachRecipeBook(screen: object, o: RecipeBookOpts): RecipeBookPanel {
  const host = screen as unknown as Host;
  const panel = new RecipeBookPanel(host, o);
  hook(host, panel);
  return panel;
}

export type { RecipeBookPanel };

// ----------------------------------------------------------------- recipe list ---

class RecipeListPanel extends SidePanel {
  private page = 0;

  constructor(sc: Host, button: { x: number; y: number }, private readonly title: string, private readonly rows: ListRow[],
    private readonly describe: (row: ListRow) => string[]) {
    super(sc, button);
  }

  hint(): string { return this.title; }

  protected size(): { w: number; h: number } {
    return { w: 22 + 5 * this.g.cell, h: Math.max(166, 13 + this.g.bar + 4 + 4 * this.g.cell + 6 + this.g.btn + 5) };
  }

  protected build(): void {
    const { px, py, pw, ph, g } = this;
    const rowH = g.rowH;
    const top = py + 24;
    const perPage = Math.max(1, Math.floor((ph - 24 - 24) / rowH));
    const pages = Math.max(1, Math.ceil(this.rows.length / perPage));
    this.page = Math.min(this.page, pages - 1);
    this.el.appendChild(panelBg(px, py, pw, ph));
    this.el.appendChild(textAt(this.title, px + pw / 2, py + 9, '#404040', 'center'));
    const s = Math.min(1.5, rowH / 22 * 1.0);
    for (let i = 0; i < perPage; i++) {
      const row = this.rows[this.page * perPage + i];
      if (!row) break;
      const y = top + i * rowH;
      this.el.appendChild(box(px + 5, y, pw - 10, rowH - 2, 'background:rgba(0,0,0,0.12);'));
      let x = px + 8;
      const step = Math.round(18 * s);
      row.inputs.forEach((st, k) => {
        if (k > 0) this.el.appendChild(textAt('+', x - 4, y + Math.floor((rowH - 2 - 8) / 2), '#404040', 'center'));
        this.el.appendChild(iconEl(st, x, y + (rowH - 2 - 16 * s) / 2, s));
        x += step;
      });
      x += 2;
      this.el.appendChild(textAt('>', x + 3, y + Math.floor((rowH - 2 - 8) / 2), '#404040', 'center'));
      x += 12;
      this.el.appendChild(iconEl(row.output, x, y + (rowH - 2 - 16 * s) / 2, s));
      this.regions.push({ x: px + 5, y, w: pw - 10, h: rowH - 2, tip: () => this.describe(row) });
    }
    const ay = py + ph - 22, ah = g.btn - 3;
    const aw = Math.max(12, Math.round(ah * 12 / 17));
    if (pages > 1) {
      this.el.appendChild(sprite('gui/sprites/recipe_book/page_backward.png', px + 8, ay, aw, ah));
      this.regions.push({ x: px + 8, y: ay, w: aw, h: ah, down: () => { this.page = (this.page - 1 + pages) % pages; this.invalidate(); } });
      this.el.appendChild(sprite('gui/sprites/recipe_book/page_forward.png', px + pw - 8 - aw, ay, aw, ah));
      this.regions.push({ x: px + pw - 8 - aw, y: ay, w: aw, h: ah, down: () => { this.page = (this.page + 1) % pages; this.invalidate(); } });
    }
    this.el.appendChild(textAt(`${this.page + 1}/${pages}`, px + pw / 2, ay + Math.floor((ah - 8) / 2), '#404040', 'center'));
  }

  override wheel(dy: number): boolean {
    if (!this.open) return false;
    this.page = Math.max(0, this.page + (dy > 0 ? 1 : -1));
    this.invalidate();
    return true;
  }
}

/** Hook a read-only recipe list (furnace, brewing, smithing) into a screen. */
export function attachRecipeList(
  screen: object, button: { x: number; y: number }, title: string, rows: ListRow[], describe: (row: ListRow) => string[],
): RecipeListPanel {
  const host = screen as unknown as Host;
  const panel = new RecipeListPanel(host, button, title, rows, describe);
  hook(host, panel);
  return panel;
}

export type { RecipeListPanel };
